import { RecaptchaVerifier, PhoneAuthProvider, linkWithCredential } from 'firebase/auth';
import { auth } from '../firebase.js';
import { renderTopbar } from '../components/topbar.js';
import { brandMarkHtml } from '../components/brandMark.js';
import { navigate } from '../router.js';
import {
  currentUser, isSchoolOwnerCached, setSchoolOwnerCached,
  signInWithGoogle, signInWithEmail, signUpWithEmail,
} from '../lib/auth.js';
import { startSchoolTrial } from '../lib/school.js';
import { friendlyError, humanizeAuthError } from '../lib/friendlyError.js';

// ─────────────────────────────────────────────────────────────────────────
// Self-serve school onboarding (#/school/start) — a PUBLIC, school-branded
// flow, deliberately separate from the ordinary #/login screen so a school
// gets its own front door. Two steps:
//   1. renderAuthStep  — signed-out: create/sign in a school-owner account
//      (branded "Start your school's free trial", not the generic login).
//   2. renderSetupStep — signed-in: school name + a mobile number verified by
//      Firebase phone OTP (linked to the account so startSchoolTrial can
//      require it server-side), then "Start free trial" → the /school
//      dashboard.
//
// NOTE (deploy config): Firebase phone auth must be enabled for the project
// and this app's domain in the Auth "Authorized domains" list, or the
// reCAPTCHA step fails. This is console configuration, not code.
// ─────────────────────────────────────────────────────────────────────────

function toE164(raw) {
  const v = String(raw || '').replace(/[^\d+]/g, '');
  if (!v) return null;
  if (v.startsWith('+')) return v.length >= 8 ? v : null;
  if (v.length === 10) return `+91${v}`;
  if (v.length > 10) return `+${v}`;
  return null;
}

function phoneErrorMessage(err) {
  const code = err?.code || '';
  if (code === 'auth/invalid-phone-number') return 'That doesn’t look like a valid mobile number.';
  if (code === 'auth/too-many-requests') return 'Too many attempts — please wait a little while and try again.';
  if (code === 'auth/code-expired') return 'That code expired — send a new one.';
  if (code === 'auth/invalid-verification-code') return 'That code isn’t right — check it and try again.';
  if (code === 'auth/credential-already-in-use' || code === 'auth/account-exists-with-different-credential') {
    return 'That mobile number is already linked to another account.';
  }
  return err?.message || 'Could not verify that number. Please try again.';
}

export function renderSchoolStart(container) {
  const user = currentUser();
  // Already a school owner — nothing to start; go to the dashboard.
  if (user && isSchoolOwnerCached()) { navigate('/school'); return () => {}; }
  container.innerHTML = '';
  return user ? renderSetupStep(container) : renderAuthStep(container);
}

// ── Step 1: school-branded sign in / sign up (signed-out visitors) ────────
function renderAuthStep(container) {
  let mode = 'signup';

  function draw() {
    container.innerHTML = `
      <div class="center-screen">
        <div class="auth-card">
          <div style="text-align:center;"><span class="pill">For schools &amp; colleges</span></div>
          <h1 style="display:flex;align-items:center;justify-content:center;gap:10px;margin-top:10px;">${brandMarkHtml()} Simustage for schools</h1>
          <p style="text-align:center;color:var(--text-dim);margin-top:-4px;font-size:0.9rem;">
            ${mode === 'signup'
              ? 'Create your school-owner account to start a free 3-month trial.'
              : 'Sign in to your school-owner account to continue.'}
          </p>
          <button class="btn" id="google-btn" style="width:100%;justify-content:center;margin-top:16px;">
            Continue with Google
          </button>
          <div class="divider">or</div>
          <form id="email-form">
            ${mode === 'signup' ? '<div class="field"><label>Your name</label><input type="text" id="name" required /></div>' : ''}
            <div class="field"><label>Work email</label><input type="email" id="email" required /></div>
            <div class="field"><label>Password</label><input type="password" id="password" required minlength="6" /></div>
            <div id="auth-error" style="color:var(--danger);font-size:0.82rem;margin-bottom:8px;"></div>
            <button class="btn btn-primary" type="submit" style="width:100%;justify-content:center;">
              ${mode === 'signup' ? 'Create school account' : 'Sign in'}
            </button>
          </form>
          <p style="text-align:center;font-size:0.85rem;margin-top:14px;">
            ${mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}
            <a href="#" id="toggle-mode" style="color:var(--accent);font-weight:600;">
              ${mode === 'signup' ? 'Sign in' : 'Create one'}
            </a>
          </p>
          <p style="text-align:center;font-size:0.82rem;margin-top:6px;">
            <a href="#/" style="color:var(--text-dim);">← Back to site</a>
          </p>
        </div>
      </div>
    `;

    const showError = (err) => {
      const el = container.querySelector('#auth-error');
      if (el) el.textContent = humanizeAuthError(err);
    };
    // On success, advance to the setup step (watchAuth also re-resolves the
    // route, so this just makes the transition immediate).
    const advance = () => renderSchoolStart(container);

    container.querySelector('#google-btn').addEventListener('click', async () => {
      try { await signInWithGoogle(); advance(); } catch (e) { showError(e); }
    });
    container.querySelector('#toggle-mode').addEventListener('click', (e) => {
      e.preventDefault();
      mode = mode === 'signup' ? 'signin' : 'signup';
      draw();
    });
    container.querySelector('#email-form').addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = container.querySelector('#email').value.trim();
      const password = container.querySelector('#password').value;
      try {
        if (mode === 'signup') {
          const name = container.querySelector('#name').value.trim();
          await signUpWithEmail(email, password, name);
        } else {
          await signInWithEmail(email, password);
        }
        advance();
      } catch (err) { showError(err); }
    });
  }

  draw();
  return () => {};
}

// ── Step 2: school name + mobile OTP + start the trial (signed-in) ────────
function renderSetupStep(container) {
  container.appendChild(renderTopbar('/school'));

  const preVerifiedPhone = currentUser()?.phoneNumber || null;

  const main = document.createElement('div');
  main.className = 'main-full';
  main.innerHTML = `
    <div class="settings-grid" style="max-width:640px;margin:0 auto;">
      <div class="settings-panel">
        <h1 style="margin-bottom:4px;">🏫 Start your school’s free trial</h1>
        <p style="color:var(--text-dim);margin-top:0;">
          Bring Simustage to your students free for 3 months — no card, no setup call.
        </p>
        <ul style="color:var(--text-dim);font-size:0.9rem;line-height:1.6;padding-left:18px;margin:12px 0;">
          <li>Viewing covered for your students, plus the Library and every free animation.</li>
          <li>Your own school dashboard with a shareable code, or add students by email.</li>
          <li>When the trial ends, top up your school wallet online and buy seats — all self-serve.</li>
        </ul>

        <div class="field">
          <label for="school-name">School / college name</label>
          <input type="text" id="school-name" maxlength="120" placeholder="e.g. Rishikul Vidyalaya, Gohana" autocomplete="organization" />
        </div>

        <div class="field" id="phone-row" style="${preVerifiedPhone ? 'display:none;' : ''}">
          <label for="school-phone">Mobile number</label>
          <div style="display:flex;gap:8px;align-items:flex-start;flex-wrap:wrap;">
            <input type="tel" id="school-phone" placeholder="+91 98765 43210" autocomplete="tel" style="flex:1;min-width:200px;" />
            <button class="btn" id="send-otp-btn" type="button">Send code</button>
          </div>
          <p style="color:var(--text-dim);font-size:0.8rem;margin:6px 0 0;">We verify this once and may call you to help you get set up.</p>
        </div>

        <div class="field" id="otp-row" style="display:none;">
          <label for="school-otp">Enter the 6-digit code</label>
          <div style="display:flex;gap:8px;align-items:flex-start;flex-wrap:wrap;">
            <input type="text" id="school-otp" inputmode="numeric" maxlength="6" placeholder="123456" style="flex:1;min-width:160px;letter-spacing:3px;" />
            <button class="btn" id="verify-otp-btn" type="button">Verify</button>
          </div>
        </div>

        <p id="phone-verified-note" style="color:var(--success);font-size:0.9rem;margin:4px 0;${preVerifiedPhone ? '' : 'display:none;'}">
          ✓ Mobile verified${preVerifiedPhone ? ` (${escapeHtml(preVerifiedPhone)})` : ''}.
        </p>

        <div id="school-start-msg" style="font-size:0.85rem;min-height:1.2em;margin:6px 0;"></div>

        <button class="btn btn-primary btn-lg" id="start-trial-btn" ${preVerifiedPhone ? '' : 'disabled'} style="width:100%;justify-content:center;">
          Start free trial
        </button>

        <div id="recaptcha-container"></div>
      </div>
    </div>
  `;
  container.appendChild(main);

  const nameInput = main.querySelector('#school-name');
  const phoneRow = main.querySelector('#phone-row');
  const phoneInput = main.querySelector('#school-phone');
  const sendBtn = main.querySelector('#send-otp-btn');
  const otpRow = main.querySelector('#otp-row');
  const otpInput = main.querySelector('#school-otp');
  const verifyBtn = main.querySelector('#verify-otp-btn');
  const verifiedNote = main.querySelector('#phone-verified-note');
  const msg = main.querySelector('#school-start-msg');
  const startBtn = main.querySelector('#start-trial-btn');

  let verified = !!preVerifiedPhone;
  let verificationId = null;
  let appVerifier = null;

  const setMsg = (text, kind = 'error') => {
    msg.style.color = kind === 'error' ? 'var(--danger)' : kind === 'success' ? 'var(--success)' : 'var(--text-dim)';
    msg.textContent = text || '';
  };
  const refreshStartEnabled = () => {
    startBtn.disabled = !(verified && nameInput.value.trim().length >= 2);
  };
  nameInput.addEventListener('input', refreshStartEnabled);

  function resetRecaptcha() {
    if (appVerifier) { try { appVerifier.clear(); } catch (e) { /* ignore */ } appVerifier = null; }
    const holder = main.querySelector('#recaptcha-container');
    if (holder) holder.innerHTML = '';
  }

  sendBtn?.addEventListener('click', async () => {
    const e164 = toE164(phoneInput.value);
    if (!e164) { setMsg('Enter a valid mobile number (with country code if outside India).'); return; }
    sendBtn.disabled = true;
    sendBtn.textContent = 'Sending…';
    setMsg('');
    try {
      resetRecaptcha();
      appVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
      const provider = new PhoneAuthProvider(auth);
      verificationId = await provider.verifyPhoneNumber(e164, appVerifier);
      otpRow.style.display = '';
      otpInput.focus();
      setMsg(`Code sent to ${e164}.`, 'muted');
      sendBtn.textContent = 'Resend code';
    } catch (err) {
      resetRecaptcha();
      setMsg(phoneErrorMessage(err));
      sendBtn.textContent = 'Send code';
    } finally {
      sendBtn.disabled = false;
    }
  });

  verifyBtn?.addEventListener('click', async () => {
    const code = String(otpInput.value || '').replace(/\D/g, '');
    if (code.length < 6 || !verificationId) { setMsg('Enter the 6-digit code we sent.'); return; }
    verifyBtn.disabled = true;
    verifyBtn.textContent = 'Verifying…';
    setMsg('');
    try {
      const cred = PhoneAuthProvider.credential(verificationId, code);
      await linkWithCredential(currentUser(), cred);
      verified = true;
      phoneRow.style.display = 'none';
      otpRow.style.display = 'none';
      verifiedNote.style.display = '';
      resetRecaptcha();
      refreshStartEnabled();
      setMsg('Mobile verified.', 'success');
    } catch (err) {
      setMsg(phoneErrorMessage(err));
      verifyBtn.disabled = false;
      verifyBtn.textContent = 'Verify';
    }
  });

  startBtn.addEventListener('click', async () => {
    const name = nameInput.value.trim();
    if (name.length < 2) { setMsg('Enter your school or college name.'); return; }
    if (!verified) { setMsg('Please verify your mobile number first.'); return; }
    startBtn.disabled = true;
    startBtn.textContent = 'Starting your trial…';
    setMsg('');
    try {
      await startSchoolTrial(name);
      setSchoolOwnerCached(currentUser()?.uid, true);
      navigate('/school');
    } catch (err) {
      setMsg(friendlyError(err, 'Could not start your trial. Please try again.'));
      startBtn.disabled = false;
      startBtn.textContent = 'Start free trial';
    }
  });

  return () => { resetRecaptcha(); };
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
